import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { employees as api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserSquare2, Plus, Pencil, Trash2, Receipt } from "lucide-react";

const EMPTY = { name: "", id_number: "", email: "", phone: "", position: "", employment_type: "Full-time", salary: 0, start_date: "", bank_name: "", account_number: "" };

export default function Employees() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.list().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (emp) => { setEditing(emp); setForm({ ...EMPTY, ...emp }); setOpen(true); };
  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? Number(e.target.value) : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.update(editing.id, form); toast.success("Employee updated"); }
      else { await api.create(form); toast.success("Employee added"); }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.remove(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;

  return (
    <div>
      <PageHeader title="Employees" subtitle="Manage employee records and generate payslips.">
        <Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-employee-button"><Plus className="w-4 h-4 mr-1.5" /> Add Employee</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={UserSquare2} title="No employees yet" description="Add employees to run payroll and generate payslips."
          action={<Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-add-employee"><Plus className="w-4 h-4 mr-1.5" /> Add Employee</Button>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((emp) => (
            <Card key={emp.id} className="p-5" data-testid={`employee-card-${emp.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-[#0B132B] text-white flex items-center justify-center font-bold text-sm">{emp.name.replace(/\[DEMO\]\s*/,"").slice(0,2).toUpperCase()}</div>
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-[#0B132B] truncate">{emp.name}</h3>
                    <p className="text-xs text-slate-500 truncate">{emp.position || "—"}</p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(emp)} data-testid={`edit-employee-${emp.id}`}><Pencil className="w-4 h-4" /></Button>
                  <ConfirmDelete onConfirm={() => remove(emp.id)} title="Delete employee?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-employee-${emp.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Salary</span><span className="font-semibold text-[#0B132B]">{money(emp.salary)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="text-slate-700">{emp.employment_type}</span></div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => nav("/payslips", { state: { employeeId: emp.id } })} data-testid={`payslip-employee-${emp.id}`}>
                <Receipt className="w-4 h-4 mr-1.5" /> Generate Payslip
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="employee-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="employee-name-input" /></div>
              <div><Label>ID Number</Label><Input value={form.id_number} onChange={set("id_number")} className="mt-1.5" data-testid="employee-id-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="employee-email-input" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="employee-phone-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Position</Label><Input value={form.position} onChange={set("position")} className="mt-1.5" data-testid="employee-position-input" /></div>
              <div><Label>Employment Type</Label><Input value={form.employment_type} onChange={set("employment_type")} className="mt-1.5" data-testid="employee-type-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Salary (R) *</Label><Input required type="number" step="0.01" value={form.salary} onChange={set("salary", true)} className="mt-1.5" data-testid="employee-salary-input" /></div>
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={set("start_date")} className="mt-1.5" data-testid="employee-start-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bank</Label><Input value={form.bank_name} onChange={set("bank_name")} className="mt-1.5" data-testid="employee-bank-input" /></div>
              <div><Label>Account No.</Label><Input value={form.account_number} onChange={set("account_number")} className="mt-1.5" data-testid="employee-account-input" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-employee-button">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
